import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { NAV_LINKS } from '../../data'
import { clearAuth, loadAuth } from '../../lib/authStorage'
import { apiGet } from '../../lib/api'
import { USER_ME } from '../../lib/endpoints'
import type { UserProfile } from '../../types'

interface NavbarProps {
  onScrollTo: (id: string) => void
  // theme + onToggleTheme accepted but unused — kept for backwards-compat with Home.tsx
  theme?: 'light' | 'dark'
  onToggleTheme?: () => void
  topOffset?: number
}

export default function Navbar({ onScrollTo, topOffset = 0 }: NavbarProps) {
  const [scrolled, setScrolled] = useState<boolean>(false)
  const [mobileOpen, setMobileOpen] = useState<boolean>(false)
  const [me, setMe] = useState<UserProfile | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40)
    handler()
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadMe() {
      const auth = loadAuth()
      if (!auth?.accessToken) {
        if (!cancelled) setMe(null)
        return
      }

      try {
        const data = await apiGet<UserProfile>(USER_ME)
        if (!cancelled) setMe(data)
      } catch {
        if (!cancelled) setMe(null)
      }
    }

    void loadMe()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!mobileOpen) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  const displayName =
    `${me?.firstName ?? ''} ${me?.lastName ?? ''}`.trim() ||
    (me?.email ? String(me.email).split('@')[0] : '') ||
    ''

  function signOut() {
    clearAuth()
    setMe(null)
    navigate('/signin', { replace: true })
  }

  return (
    <>
      <motion.nav
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'py-2.5' : 'py-4'
        }`}
        style={{
          top: topOffset,
          backgroundColor: scrolled ? 'rgba(251,230,212,0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(14px) saturate(140%)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(14px) saturate(140%)' : 'none',
          borderBottom: scrolled ? '2px solid #111' : '2px solid transparent',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-[auto_1fr_auto] items-center gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 no-underline group" aria-label="Nudge home">
            <img
              src="/logo.svg"
              alt=""
              className="w-8 h-8 transition-transform duration-200 group-hover:scale-110"
              aria-hidden
            />
            <span
              className="font-extrabold text-2xl tracking-tight"
              style={{ color: '#111', letterSpacing: '-0.02em' }}
            >
              Nudge
            </span>
          </Link>

          {/* Nav links — desktop */}
          <div className="hidden md:flex items-center justify-center gap-9">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => onScrollTo(link.id)}
                className="nudge-nav-link text-sm font-bold"
                style={{ color: '#111' }}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA + actions */}
          <div className="flex items-center justify-end gap-2.5">
            {me ? (
              <>
                <Link
                  to="/dashboard"
                  className="nudge-cta-btn hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold transition-transform"
                  style={{
                    backgroundColor: 'var(--accent)',
                    color: '#fff',
                    textDecoration: 'none',
                    border: '2px solid #111',
                    borderRadius: 999,
                    boxShadow: '2px 2px 0 #111',
                  }}
                  aria-label="Open dashboard"
                >
                  Dashboard
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </Link>
                <div
                  className="hidden sm:flex items-center gap-2 pl-1.5 pr-3 py-1"
                  style={{
                    backgroundColor: '#fff',
                    border: '2px solid #111',
                    borderRadius: 999,
                    color: '#111',
                  }}
                  title={me.email ?? undefined}
                >
                  <div
                    className="h-7 w-7 flex items-center justify-center"
                    style={{
                      borderRadius: 999,
                      backgroundColor: 'var(--accent)',
                      backgroundImage: me.avatarUrl ? `url(${me.avatarUrl})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      fontSize: 11,
                      fontWeight: 800,
                      color: '#fff',
                    }}
                  >
                    {me.avatarUrl ? null : (displayName || 'U').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="max-w-[120px] truncate text-sm font-bold" style={{ color: '#111' }}>
                    {displayName || 'Account'}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={signOut}
                  className="nudge-cta-ghost hidden sm:inline-flex items-center px-4 py-2 text-sm font-bold transition-transform"
                  style={{
                    color: '#111',
                    backgroundColor: '#fff',
                    border: '2px solid #111',
                    borderRadius: 999,
                    boxShadow: '2px 2px 0 #111',
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/signin"
                  className="hidden sm:inline-flex items-center px-3.5 py-2 text-sm font-bold transition-colors"
                  style={{ color: '#111', textDecoration: 'none' }}
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="nudge-cta-btn hidden sm:inline-flex items-center px-4 py-2 text-sm font-bold transition-transform"
                  style={{
                    backgroundColor: 'var(--accent)',
                    color: '#fff',
                    textDecoration: 'none',
                    border: '2px solid #111',
                    borderRadius: 999,
                    boxShadow: '2px 2px 0 #111',
                  }}
                >
                  Get started
                </Link>
              </>
            )}

            {/* Mobile menu trigger */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              style={{
                width: 38,
                height: 38,
                borderRadius: 999,
                border: '2px solid #111',
                backgroundColor: '#fff',
                color: '#111',
                boxShadow: '2px 2px 0 #111',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <style>{`
          .nudge-nav-link {
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px 0;
            text-decoration: none;
            transition: color 180ms ease;
            position: relative;
          }
          .nudge-nav-link::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: -2px;
            height: 2px;
            background-color: var(--accent);
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 180ms ease;
          }
          .nudge-nav-link:hover::after {
            transform: scaleX(1);
          }
          .nudge-cta-btn:hover,
          .nudge-cta-ghost:hover {
            transform: translate(-1px, -1px);
            box-shadow: 3px 3px 0 #111 !important;
          }
          .nudge-cta-btn:active,
          .nudge-cta-ghost:active {
            transform: translate(1px, 1px);
            box-shadow: 0 0 0 #111 !important;
          }
        `}</style>
      </motion.nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] md:hidden"
          style={{ backgroundColor: 'rgba(17,17,17,0.45)' }}
          onClick={() => setMobileOpen(false)}
        >
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
            className="absolute top-0 right-0 bottom-0 w-[78%] max-w-[340px] flex flex-col"
            style={{ backgroundColor: '#FBE6D4', borderLeft: '2px solid #111' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-6 py-5"
              style={{ borderBottom: '2px solid #111' }}
            >
              <div className="flex items-center gap-2.5">
                <img src="/logo.svg" alt="" className="w-8 h-8" aria-hidden />
                <span
                  className="font-extrabold text-xl"
                  style={{ color: '#111', letterSpacing: '-0.02em' }}
                >
                  Nudge
                </span>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="inline-flex items-center justify-center"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 999,
                  border: '2px solid #111',
                  backgroundColor: '#fff',
                  color: '#111',
                  boxShadow: '2px 2px 0 #111',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <nav className="flex flex-col gap-1 px-3 py-5" aria-label="Mobile primary">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => {
                    setMobileOpen(false)
                    onScrollTo(link.id)
                  }}
                  className="text-left px-4 py-3 text-base font-bold"
                  style={{ color: '#111', background: 'transparent', borderRadius: 12 }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(17,17,17,0.06)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {link.label}
                </button>
              ))}
              {me ? (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 text-base font-bold"
                  style={{ color: '#111', textDecoration: 'none', borderRadius: 12 }}
                >
                  Dashboard
                </Link>
              ) : null}
            </nav>

            <div className="mt-auto px-6 pb-7 flex flex-col gap-3">
              {me ? (
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false)
                    signOut()
                  }}
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-bold"
                  style={{
                    backgroundColor: '#fff',
                    color: '#111',
                    border: '2px solid #111',
                    borderRadius: 999,
                    boxShadow: '3px 3px 0 #111',
                  }}
                >
                  Sign out
                </button>
              ) : (
                <>
                  <Link
                    to="/signin"
                    onClick={() => setMobileOpen(false)}
                    className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-bold"
                    style={{
                      backgroundColor: '#fff',
                      color: '#111',
                      border: '2px solid #111',
                      borderRadius: 999,
                      boxShadow: '3px 3px 0 #111',
                      textDecoration: 'none',
                    }}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setMobileOpen(false)}
                    className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-bold"
                    style={{
                      backgroundColor: 'var(--accent)',
                      color: '#fff',
                      border: '2px solid #111',
                      borderRadius: 999,
                      boxShadow: '3px 3px 0 #111',
                      textDecoration: 'none',
                    }}
                  >
                    Get started
                  </Link>
                </>
              )}
            </div>
          </motion.aside>
        </motion.div>
      )}
    </>
  )
}
